/**
 * @file        src/api/client.js
 * @description Axios with silent 401 → /auth/refresh → replay flow.
 *              Access token in memory; refresh token is the backend httpOnly cookie.
 * @author      Kinetika Labs
 * @created     2026-06-22
 * @changelog
 *   - 2026-06-22 Claude — Initial.
 *   - 2026-07-15 Claude — Production default is /admin-api/api, not /api.
 *     The Jenkins build sets no VITE_API_BASE_URL, so every deployed bundle fell
 *     back to /api — which nginx routes to the CUSTOMER backend (no /auth routes →
 *     404 on login/refresh). Behind nginx the admin API lives at /admin-api/api, so
 *     that is the built-in production default. Dev is untouched: `npm run dev` sets
 *     PROD=false and keeps /api, which vite.config proxies to :4000.
 *   - 2026-08-04 Claude — In-flight request counter + onNetworkActivity()
 *     subscription, feeding the global top progress bar (<LoadBar />). Counted
 *     here at the axios layer so EVERY call shows activity — mutations and
 *     direct get/post calls included, not just React Query fetches. Pass
 *     `{ silent: true }` in a request config to opt out (e.g. typeahead that
 *     has its own inline indicator).
 *   - 2026-08-05 Claude — A cross-tab refresh race (two tabs rotating the
 *     same refresh cookie near-simultaneously) made the losing tab force-
 *     logout even though the backend's grace window kept the session alive
 *     (see auth.service.js). doRefresh() now retries once on the backend's
 *     REFRESH_RACE code; exported refreshSilently() so bootstrap() also goes
 *     through this path instead of a raw post('/auth/refresh').
 *   - 2026-09-15 Claude — (1) A wrong password on a re-verify dialog (wallet
 *     credit/refund, credit-due settle) is a 401 BAD_CREDENTIALS, which the
 *     interceptor treated as an expired token: it rotated the session, replayed
 *     the same wrong password, and force-logged-out if that refresh failed. It
 *     now passes BAD_CREDENTIALS straight to the caller. (2) apiErrorMessage()
 *     moved here from CorporateAccounts.jsx so every screen reads the
 *     {error:{code,message,fields}} shape the same way — a dozen modals put
 *     that object straight into React state and blanked the whole app.
 */
import axios from 'axios';

// Override with VITE_API_BASE_URL if the deployment mounts the API elsewhere.
const BASE_URL =
  import.meta.env.VITE_API_BASE_URL || (import.meta.env.PROD ? '/admin-api/api' : '/api');

let accessToken = null;
let refreshPromise = null;
let onForceLogout = () => {};

export function setAccessToken(t) { accessToken = t; }
export function onUnauthorized(handler) { onForceLogout = handler; }

export const api = axios.create({ baseURL: BASE_URL, withCredentials: true });

// ---- in-flight counter (drives the global <LoadBar />) ----------------------
// The count moves in the interceptors below, so anything using this axios
// instance is covered. The decrement interceptor is registered FIRST so a
// 401-refresh replay (which re-enters the request interceptor) never shows a
// double count.
let inflight = 0;
const netListeners = new Set();

/** Subscribe to the in-flight request count. Returns an unsubscribe fn. */
export function onNetworkActivity(listener) {
  netListeners.add(listener);
  listener(inflight);
  return () => netListeners.delete(listener);
}

function track(delta) {
  inflight = Math.max(0, inflight + delta);
  for (const l of netListeners) l(inflight);
}

api.interceptors.request.use((config) => {
  if (accessToken && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }
  if (!config.silent) { config._tracked = true; track(1); }
  return config;
});

api.interceptors.response.use(
  (res) => { if (res.config?._tracked) { res.config._tracked = false; track(-1); } return res; },
  (err) => {
    if (err.config?._tracked) { err.config._tracked = false; track(-1); }
    return Promise.reject(err);
  },
);

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// A losing tab in a cross-tab refresh race gets REFRESH_RACE (see
// auth.service.js's grace window) — the winning tab's rotation is already
// live, so one short retry recovers the session instead of forcing logout.
async function doRefresh(isRetry) {
  try {
    const res = await axios.post(`${BASE_URL}/auth/refresh`, null, { withCredentials: true });
    accessToken = res.data?.data?.accessToken || null;
    return accessToken;
  } catch (err) {
    if (!isRetry && err.response?.data?.error?.code === 'REFRESH_RACE') {
      await sleep(400);
      return doRefresh(true);
    }
    throw err;
  }
}

async function refreshOnce() {
  if (!refreshPromise) {
    refreshPromise = doRefresh(false).finally(() => { refreshPromise = null; });
  }
  return refreshPromise;
}

// Bootstrap-on-mount uses this too (instead of a raw post('/auth/refresh'))
// so a cold multi-tab load also gets the REFRESH_RACE retry, not just the
// 401-interceptor path.
export async function refreshSilently() {
  try { return await refreshOnce(); } catch (_) { return null; }
}

api.interceptors.response.use(
  (res) => res,
  async (err) => {
    const status = err.response?.status;
    const original = err.config || {};
    const url = original.url || '';
    const isAuthCall = url.includes('/auth/refresh') || url.includes('/auth/login');

    // BAD_CREDENTIALS = the password typed into a confirm dialog was wrong; the
    // session itself is fine, so refreshing and replaying would only resend it.
    const badPassword = err.response?.data?.error?.code === 'BAD_CREDENTIALS';

    if (status === 401 && !isAuthCall && !badPassword && !original._retried) {
      original._retried = true;
      try {
        const fresh = await refreshOnce();
        if (fresh) {
          original.headers = { ...(original.headers || {}), Authorization: `Bearer ${fresh}` };
          return api.request(original);
        }
      } catch (_) { /* fall through */ }
      accessToken = null;
      onForceLogout();
    }
    return Promise.reject(err);
  },
);

/**
 * Human-readable message from a failed API call. Reads both error shapes —
 * {error:{code,message,fields}} from the global error handler and the plain
 * {error:'…'} a few routes still return — and never returns an object, so the
 * result is always safe to render. Field messages come first: a schema
 * failure's top-level message is only "Invalid body", which names no field.
 */
export function apiErrorMessage(e, fallback) {
  const d = e?.response?.data;
  if (typeof d?.error === 'string') return d.error;
  const fields = d?.error?.fields;
  if (fields && Object.keys(fields).length) {
    return Object.entries(fields).map(([k, v]) => `${k}: ${v}`).join(' · ');
  }
  return d?.error?.message || d?.message || fallback;
}

export async function get(url, config)   { const r = await api.get(url, config);   return r.data; }
export async function post(url, body, c) { const r = await api.post(url, body, c); return r.data; }
export async function patch(url, body, c){ const r = await api.patch(url, body, c); return r.data; }
export async function put(url, body, c)  { const r = await api.put(url, body, c);   return r.data; }
export async function del(url, c)        { const r = await api.delete(url, c);    return r.data; }
